import { PropsWithChildren } from 'react';
import { FieldError, Merge } from 'react-hook-form';
import { InputProps } from 'react-select';
import { TextStyle } from '../../../theme/common';
import {
  Flex,
  FormControl,
  FormControlProps,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HtmlParser,
  Paragraph
} from '../../atoms';

type HelperTextRenderer = (props: { helperText: string }) => JSX.Element;

export type FormFieldProps = Pick<InputProps, 'isDisabled' | 'name' | 'type'> &
  FormControlProps &
  PropsWithChildren<{
    helperText?: string;
    helperTextRenderer?: HelperTextRenderer;
    label?: string;
    labelTextStyle?: TextStyle;
    sublabel?: string;
    error?: FieldError | Merge<FieldError, Array<FieldError | undefined>>;
    /** @deprecated in favour of the 'helperText' prop*/
    extraLabel?: string;
  }>;

function DefaultHelperText(props: { helperText: string }): JSX.Element {
  return <FormHelperText>{props.helperText}</FormHelperText>;
}

function FormFieldWrapper({
  label,
  labelTextStyle,
  helperText,
  error,
  isInvalid,
  id,
  name,
  children,
  sublabel,
  helperTextRenderer,
  extraLabel,
  ...rest
}: FormFieldProps): JSX.Element {
  const renderer: HelperTextRenderer = helperTextRenderer ?? DefaultHelperText;
  return (
    <FormControl {...rest} isInvalid={isInvalid || !!error}>
      {label && (
        <FormLabel htmlFor={id ?? name} textStyle={labelTextStyle ?? 'H300'}>
          {!rest.isRequired ? (
            <Flex direction={'row'} align="baseline">
              {label}
              {extraLabel && (
                <Paragraph color="palette_gray.60" size="sm" ml="space_unit.x1">
                  {`(${extraLabel})`}
                </Paragraph>
              )}
            </Flex>
          ) : (
            label
          )}
        </FormLabel>
      )}
      {sublabel && (
        <Paragraph color="palette_gray.60" size="sm" mb="space_unit.x1">
          <HtmlParser html={sublabel} />
        </Paragraph>
      )}

      {children}
      {error && (
        <FormErrorMessage>
          {error.message || error.root?.message}
        </FormErrorMessage>
      )}
      {!error && helperText && renderer({ helperText })}
    </FormControl>
  );
}

export const FormField = FormFieldWrapper;
